
import { defineComponent, ref, Ref, computed, onMounted, inject } from "vue";
import axios from "axios";
import { store } from "@/store";
import CloseSVG from "@/components/CloseSVG.vue";
import { setCanvasJson } from "@/canvas-api/ConvertCanvas";
import { prepareArtwork } from "@/composables/usePrepareSaveArtwork";
import { getEditorUrlParam } from "@/composables/useParseURL";
import usePrintPreview from "@/composables/usePrintPreview";
import { ActionTypes } from "@/store/actions";
import { CurrentLabel } from "@/types";

export default defineComponent({
  name: "SaveLabel",
  components: {
    CloseSVG,
  },
  setup() {
    const closeModal = inject("closeModal") as () => void;
    const bounceTransition = inject("bounceTransition") as Ref<string>;
    const fadeTransition = inject("fadeTransition") as Ref<string>;

    const editorParams = computed(() => store.getters.editorParams);
    const userArtworkFetch = computed(() => store.getters.userArtworkFetch);
    const currentLabel = computed<CurrentLabel>(() => store.getters.currentLabel);
    const currentLabelMaterialId = computed<CurrentLabel>(
      () => store.getters.currentLabelMaterialId
    );
    const currentLabelType = computed<string>(() => store.getters.currentLabelType);

    const isRotated = computed<boolean>(() =>
      store.getters.canvasOrientation === "rotated" ? true : false
    );
    const getDefaultOrientation = computed<string>(() =>
      store.getters.currentLabel.width >= store.getters.currentLabel.height
        ? "landscape"
        : "portrait"
    );
    const currentOrientation = computed<string>(() =>
      isRotated.value
        ? getDefaultOrientation.value === "landscape"
          ? "portrait"
          : "landscape"
        : getDefaultOrientation.value
    );

    const title = ref("");
    const saveAsNew = ref(false);
    const artworkName = ref("Blank");
    onMounted(() => {
      if (userArtworkFetch.value.artwork && Object.keys(userArtworkFetch.value.artwork).length) {
        artworkName.value = userArtworkFetch.value.artwork.title;
      }

      //Trigger rerender on mount
      title.value = "Save Your Design";
    });

    const loading = ref(false);
    const handleSaveArtwork = async () => {
      loading.value = true;
      const labelGroup = await usePrintPreview();
      const canvasAsJSON = await setCanvasJson();

      if (labelGroup && canvasAsJSON) {
        const svg = await prepareArtwork(labelGroup, isRotated.value);

        try {
          // prettier-ignore
          const data = {
            svg,
            id: null,
            title: artworkName.value,
            width: currentLabel.value.width,
            height: currentLabel.value.shape_id === 10 || currentLabel.value.shape_id === 3 ? currentLabel.value.width : currentLabel.value.height,
            shape_id: currentLabel.value.shape_id,
            label_id: currentLabel.value.label_id,
            corners: currentLabel.value.corners,
            corner_radius: currentLabel.value.corner_radius,
            editor: "full",
            editorvue: "1",
            blank_editor: "0",
            material_id: currentLabelMaterialId.value ? currentLabelMaterialId.value : getEditorUrlParam("material_id").length ? getEditorUrlParam("material_id") : "1",
            rush_id: getEditorUrlParam("rush_id").length ? getEditorUrlParam("rush_id") : "4",
            product_id: editorParams.value.product_id,
            orientation: currentOrientation.value,
            json: canvasAsJSON,
          };

          if (userArtworkFetch.value.artwork && !saveAsNew.value) {
            data.id = userArtworkFetch.value.artwork.id;
          }

          let res = await axios.post(
            `${process.env.VUE_APP_API_URL}/editor/artworks/ajax.php?action=save`,
            data
          );

          const response = {
            isFetching: false,
            payload: {
              fabric_objects: JSON.parse(canvasAsJSON),
              artwork: res.data,
            },
            error: "",
          };

          if (currentLabelType.value === "printed") {
            const data = res.data;
            if (data.hash && data.material_id && data.label && data.label.name) {
              const url = `${window.location.origin}/label-design/${currentLabelType.value}/${data.label.name}?hash=${data.hash}&material_id=${data.material_id}`;

              history.pushState(undefined, "", url);
            }
          }

          store.dispatch(ActionTypes.SET_USER_ARTWORK_FETCH, response);

          store.dispatch(ActionTypes.SET_MODAL, 'saveConfirmation');

          loading.value = false;
          saveAsNew.value = false;
        } catch (e) {
          console.log(e);

          loading.value = false;
        }
      }
    };

    return {
      userArtworkFetch,
      closeModal,
      fadeTransition,
      bounceTransition,
      title,
      saveAsNew,
      artworkName,
      loading,
      handleSaveArtwork,
    };
  },
});
